// Error message utilities for user-friendly error display
// All messages in Thai to match UI language

import { ApiError, RichMenuErrorCode, ImportType } from '../types/errors'

/**
 * RichMenu error code to Thai message mapping
 */
export const richMenuErrorMessages: Record<RichMenuErrorCode, string> = {
  'CREATE_FAILED': 'ไม่สามารถสร้าง Rich Menu ได้ กรุณาลองใหม่อีกครั้ง',
  'UPDATE_FAILED': 'ไม่สามารถอัพเดท Rich Menu ได้ กรุณาลองใหม่อีกครั้ง',
  'DELETE_FAILED': 'ไม่สามารถลบ Rich Menu ได้ กรุณาลองใหม่อีกครั้ง',
  'IMAGE_UPLOAD_FAILED': 'ไม่สามารถอัพโหลดรูปภาพได้ กรุณาตรวจสอบไฟล์และลองใหม่',
  'NOT_FOUND': 'ไม่พบ Rich Menu ที่ต้องการ',
  'INVALID_PAYLOAD': 'ข้อมูลที่ส่งไม่ถูกต้อง กรุณาตรวจสอบและลองใหม่',
}

/**
 * Import type to Thai error message mapping
 */
export const importErrorMessages: Record<ImportType, string> = {
  'PRODUCT_CATALOG': 'การนำเข้า Product Catalog ล้มเหลว',
  'PRODUCT_CAMPAIGN': 'การนำเข้า Product Campaign ล้มเหลว',
  'PRODUCT_CONSULTANT': 'การนำเข้า Product Consultant ล้มเหลว',
  'SALES_OUT_DAILY': 'การนำเข้า Sales Out Daily ล้มเหลว',
  'SALES_TARGET': 'การนำเข้า Sales Target ล้มเหลว',
}

/**
 * HTTP status code to Thai error message mapping
 */
export const httpErrorMessages: Record<number, string> = {
  400: 'ข้อมูลไม่ถูกต้อง กรุณาตรวจสอบและลองใหม่',
  401: 'กรุณาเข้าสู่ระบบใหม่',
  403: 'คุณไม่มีสิทธิ์ดำเนินการนี้',
  404: 'ไม่พบข้อมูลที่ต้องการ',
  413: 'ไฟล์มีขนาดใหญ่เกินไป (สูงสุด 10MB)',
  500: 'เกิดข้อผิดพลาดในระบบ กรุณาลองใหม่ภายหลัง',
  502: 'ไม่สามารถเชื่อมต่อกับเซิร์ฟเวอร์ได้',
  503: 'ระบบไม่พร้อมให้บริการชั่วคราว',
  504: 'การเชื่อมต่อหมดเวลา กรุณาลองใหม่',
}

/**
 * Get user-friendly error message from any error type
 * @param error - Error object (ApiError, Error, or unknown)
 * @returns Thai error message string
 */
export const getErrorMessage = (error: unknown): string => {
  const withDetails = (message: string): string => {
    if (error instanceof ApiError && error.details) {
      return `${error.details}`
    }
    return message
  }

  // Handle ApiError with structured error information
  if (error instanceof ApiError) {
    // Check for RichMenu error codes
    if (error.errorCode && error.errorCode in richMenuErrorMessages) {
      return withDetails(richMenuErrorMessages[error.errorCode as RichMenuErrorCode])
    }

    // Check for Import error types
    if (error.importType && error.importType in importErrorMessages) {
      return withDetails(importErrorMessages[error.importType as ImportType])
    }

    // Check for HTTP status messages
    if (error.status && error.status in httpErrorMessages) {
      return withDetails(httpErrorMessages[error.status])
    }

    // Fall back to error message from server
    return withDetails(error.message || 'เกิดข้อผิดพลาด กรุณาลองใหม่อีกครั้ง')
  }

  // Handle standard Error objects
  if (error instanceof Error) {
    // Check for timeout errors
    if (error.message.includes('timeout') || error.name === 'AbortError') {
      return 'การเชื่อมต่อหมดเวลา กรุณาลองใหม่'
    }

    // Check for network errors
    if (error.message.includes('network') || error.message.includes('fetch')) {
      return 'ไม่สามารถเชื่อมต่อกับเซิร์ฟเวอร์ได้'
    }

    return error.message
  }

  // Default error message
  return 'เกิดข้อผิดพลาด กรุณาลองใหม่อีกครั้ง'
}

/**
 * Get field-level error messages from ApiError
 * @param error - ApiError with potential field errors
 * @returns Record of field names to error messages, or empty object
 */
export const getFieldErrors = (error: unknown): Record<string, string> => {
  if (error instanceof ApiError && error.fieldErrors) {
    return error.fieldErrors
  }
  return {}
}

/**
 * Check if error is an authentication error requiring re-login
 * @param error - Any error object
 * @returns true if user should be redirected to login
 */
export const isAuthError = (error: unknown): boolean => {
  if (error instanceof ApiError) {
    return error.isAuthenticationError()
  }
  return false
}

/**
 * Check if error is a permission/authorization error
 * @param error - Any error object
 * @returns true if user lacks permission
 */
export const isPermissionError = (error: unknown): boolean => {
  if (error instanceof ApiError) {
    return error.isAuthorizationError()
  }
  return false
}
